import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";

const sites = [
  {
    image:
      "https://images.unsplash.com/photo-1694521787162-5373b598945c?crop=entropy&cs=srgb&fm=jpg&q=85",
    title: "Commercial Build-Out",
    location: "Honolulu, HI",
    category: "Commercial",
  },
  {
    image:
      "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&q=85",
    title: "Mixed-Use Development",
    location: "Waipahu, HI",
    category: "Construction",
  },
  {
    image:
      "https://images.pexels.com/photos/392031/pexels-photo-392031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    title: "Industrial Facility Wiring",
    location: "Kapolei, HI",
    category: "Industrial",
  },
  {
    image:
      "https://images.unsplash.com/photo-1622467731046-c22f11a94cbb?crop=entropy&cs=srgb&fm=jpg&q=85",
    title: "Residential New Build",
    location: "Kailua, HI",
    category: "Residential",
  },
  {
    image:
      "https://images.pexels.com/photos/4170184/pexels-photo-4170184.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    title: "Structural Steel Fit-Out",
    location: "Pearl City, HI",
    category: "Commercial",
  },
  {
    image:
      "https://images.unsplash.com/photo-1707655536176-a2bc5e00cba9?crop=entropy&cs=srgb&fm=jpg&q=85",
    title: "Panel Upgrade & Service",
    location: "Aiea, HI",
    category: "Service",
  },
];

const BuildingSites = () => {
  return (
    <section className="relative py-24 bg-white overflow-hidden">
      {/* ambient blob */}
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full blur-3xl opacity-[0.08] pointer-events-none"
        style={{
          background: "radial-gradient(ellipse, #10b981 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6">
        {/* Heading */}
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 items-end mb-14">
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] mb-4 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">
              Building Sites in Action
            </span>
            <h2
              className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.1]"
              style={{ color: "#0a2e1f" }}
            >
              Projects on the ground <br className="hidden md:block" />
              across <span className="text-emerald-700">the Pacific</span>
            </h2>
          </div>
          <p className="text-slate-600 leading-relaxed lg:pb-2">
            A glimpse into recent job sites — commercial build-outs, industrial
            facilities, residential new-builds and service upgrades. Every site
            is delivered on-spec, on-time and on-budget.
          </p>
        </div>

        {/* Masonry-style grid: 3 rows, staggered heights */}
        <div className="grid grid-cols-2 md:grid-cols-6 grid-rows-2 gap-4 md:gap-5 mb-10">
          {/* Large feature tile */}
          <div className="col-span-2 md:col-span-3 row-span-2 group relative rounded-3xl overflow-hidden min-h-[380px] md:min-h-[520px]">
            <img
              src={sites[0].image}
              alt={sites[0].title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a2e1f]/90 via-[#0a2e1f]/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white mb-3 backdrop-blur-sm" style={{ background: "rgba(16,185,129,0.9)" }}>
                {sites[0].category}
              </span>
              <h3 className="text-white text-2xl md:text-3xl font-bold mb-2 tracking-tight">
                {sites[0].title}
              </h3>
              <p className="text-emerald-100/90 text-sm flex items-center gap-1.5">
                <MapPin size={14} /> {sites[0].location}
              </p>
            </div>
          </div>

          {/* Medium tiles */}
          {sites.slice(1, 3).map((s, idx) => (
            <div
              key={idx}
              className="col-span-1 md:col-span-3 group relative rounded-3xl overflow-hidden min-h-[200px] md:min-h-[250px]"
            >
              <img
                src={s.image}
                alt={s.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a2e1f]/85 via-[#0a2e1f]/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white mb-2 backdrop-blur-sm" style={{ background: "rgba(16,185,129,0.9)" }}>
                  {s.category}
                </span>
                <h3 className="text-white text-lg font-bold mb-1 tracking-tight">
                  {s.title}
                </h3>
                <p className="text-emerald-100/80 text-xs flex items-center gap-1">
                  <MapPin size={12} /> {s.location}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Secondary row: 3 equal tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-12">
          {sites.slice(3).map((s, idx) => (
            <div
              key={idx}
              className="group relative rounded-3xl overflow-hidden aspect-[4/3]"
            >
              <img
                src={s.image}
                alt={s.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a2e1f]/85 via-[#0a2e1f]/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white mb-2 backdrop-blur-sm" style={{ background: "rgba(16,185,129,0.9)" }}>
                  {s.category}
                </span>
                <h3 className="text-white text-base font-bold mb-1 tracking-tight">
                  {s.title}
                </h3>
                <p className="text-emerald-100/80 text-xs flex items-center gap-1">
                  <MapPin size={12} /> {s.location}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/request-info"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.03] transition-all"
            style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
          >
            Start your project with us <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BuildingSites;
